/**
 * Data contract — TypeScript models mirroring `sully-k/claude-brain-mcp`
 * `src/factory-types.ts` (Phase 2a Factory READ-MODEL + COMMANDS), which itself
 * mirrors the Supabase migration `20260719000000_factory_read_model.sql`.
 *
 * These are the raw **snake_case** shapes as returned by PostgREST — the web
 * client decodes them straight off the wire (no camelCase remap, unlike the
 * Swift app's CodingKeys, since JS reads snake_case fields directly).
 *
 * WHO WRITES / WHO READS
 *   * The engine (agent-orchestrator daemon) writes every `factory_*` table with
 *     the SERVICE ROLE (BYPASSRLS). It owns the read-model.
 *   * Apps READ all tables scoped by RLS to their own `tenant_id`, and INSERT
 *     ONLY `factory_commands` (see FactoryCommandInsert).
 */

/** `tenant_id` is a `brain_users.id` (uuid). */
export type TenantId = string;

/** `factory_commands.kind` — Postgres enum `factory_command_kind`. */
export type FactoryCommandKind =
  | 'enqueue_task'
  | 'resolve_approval'
  | 'accept_proposal'
  | 'post_chat';

/** `factory_commands.status` — check-constrained lifecycle. */
export type FactoryCommandStatus = 'queued' | 'applied' | 'error';

/** JSON payload shape (jsonb columns: `args`, `payload`, `result`). */
export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[];

/** Mirror of engine tasks (`public.factory_tasks`). Engine-written, app-read. */
export interface FactoryTaskRow {
  id: string;
  tenant_id: TenantId;
  external_id: string;
  department: string | null;
  priority: number | null;
  /** queued|running|blocked|done|failed|cancelled|… (not DB-constrained). */
  status: string | null;
  source: string | null;
  worktree_repo: string | null;
  branch: string | null;
  log_path: string | null;
  budget_usd: number | null;
  runtime_sec: number | null;
  current_action: string | null;
  result: string | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
}

/** PR-review sub-state (`public.factory_pr_tasks`). Engine-written, app-read. */
export interface FactoryPrTaskRow {
  id: string;
  tenant_id: TenantId;
  pr_key: string;
  status: string | null;
  verdict: string | null;
  sensitive: boolean;
  preview_url: string | null;
  clickup_ticket_id: string | null;
  created_at: string;
  updated_at: string;
}

/** The "needs you" queue (`public.factory_approvals`). Engine-written, app-read. */
export interface FactoryApprovalRow {
  id: string;
  tenant_id: TenantId;
  external_id: string;
  task_ref: string | null;
  tool: string | null;
  args: Json;
  status: string | null;
  decision: string | null;
  reason: string | null;
  created_at: string;
  updated_at: string;
}

/** Append-only live feed (`public.factory_events`). Engine-written, app-read. */
export interface FactoryEventRow {
  id: number;
  tenant_id: TenantId;
  type: string;
  scope: string | null;
  payload: Json;
  event_ts: string;
  created_at: string;
}

/** One row per tenant (`public.factory_health`). Engine-written, app-read. */
export interface FactoryHealthRow {
  tenant_id: TenantId;
  daemon_alive: boolean;
  last_heartbeat: string | null;
  watchdog_state: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Steer channel row (`public.factory_commands`). Apps INSERT (status=queued);
 * the engine drains and flips status to applied|error.
 */
export interface FactoryCommandRow {
  id: string;
  tenant_id: TenantId;
  kind: FactoryCommandKind;
  payload: Json;
  status: FactoryCommandStatus;
  result: Json | null;
  created_at: string;
  applied_at: string | null;
  updated_at: string;
}

/**
 * The ONLY app-writable shape. RLS enforces `tenant_id = current_tenant_id()`
 * on INSERT; status/timestamps default server-side, so apps supply
 * `kind` + `payload` (+ their own `tenant_id`).
 */
export interface FactoryCommandInsert {
  tenant_id: TenantId;
  kind: FactoryCommandKind;
  payload?: Json;
}

/** Table-name → Row map, mirroring `FactoryTables`. */
export interface FactoryTables {
  factory_tasks: FactoryTaskRow;
  factory_pr_tasks: FactoryPrTaskRow;
  factory_approvals: FactoryApprovalRow;
  factory_events: FactoryEventRow;
  factory_health: FactoryHealthRow;
  factory_commands: FactoryCommandRow;
}

export type FactoryTableName = keyof FactoryTables;

/** Table name constants — the single source of truth for PostgREST endpoints. */
export const FactoryTable = {
  tasks: 'factory_tasks',
  prTasks: 'factory_pr_tasks',
  approvals: 'factory_approvals',
  events: 'factory_events',
  health: 'factory_health',
  commands: 'factory_commands',
} as const satisfies Record<string, FactoryTableName>;

// MARK: - UI-derived helpers (mirror the Swift app's TaskStatus convenience)

export type TaskStatus =
  | 'queued'
  | 'running'
  | 'blocked'
  | 'done'
  | 'failed'
  | 'cancelled'
  | 'unknown';

const KNOWN_TASK_STATUSES: TaskStatus[] = [
  'queued',
  'running',
  'blocked',
  'done',
  'failed',
  'cancelled',
];

/** Best-effort case over the free-text `factory_tasks.status` column. */
export function taskStatus(raw: string | null | undefined): TaskStatus {
  const s = (raw ?? '').toLowerCase();
  return (KNOWN_TASK_STATUSES as string[]).includes(s) ? (s as TaskStatus) : 'unknown';
}

/** Human-readable one-liner for a jsonb value (mirror of JSONValue.displayString). */
export function jsonDisplayString(value: Json | undefined): string {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (Array.isArray(value)) return value.map(jsonDisplayString).join(', ');
  return Object.entries(value)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => `${k}: ${jsonDisplayString(v as Json)}`)
    .sort()
    .join(', ');
}
